
import React from "react";
import FormField from "@/components/reagents/FormField";
import AmountInput from "@/components/reagents/AmountInput";
import DescriptionField from "@/components/reagents/DescriptionField";
import CategorySelect from "@/components/reagents/CategorySelect";
import ImageUploadField from "@/components/reagents/ImageUploadField";
import StorageConditionsField from "@/components/reagents/StorageConditionsField";
import DatePickerField from "@/components/reagents/DatePickerField";
import { ReagentFormData } from "@/types/reagent.types";
import { ReagentFormErrors } from "@/utils/reagentFormValidation";

interface NewReagentFormContentProps {
  formData: ReagentFormData;
  errors: ReagentFormErrors;
  categories: Array<{id: string, name: string}>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (name: string, value: string) => void;
  handleFileChange: (file: File | null) => void;
  handleDateChange: (name: string, date: Date | null) => void;
}

const NewReagentFormContent = ({ 
  formData, 
  errors, 
  categories, 
  handleChange, 
  handleSelectChange, 
  handleFileChange,
  handleDateChange
}: NewReagentFormContentProps) => {
  // Create a handler to convert ChangeEvent to File
  const handleImageUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("NewReagentFormContent - Image upload change triggered");
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      console.log("NewReagentFormContent - File found:", {
        name: file.name,
        size: file.size,
        type: file.type
      });
      handleFileChange(e.target.files[0]);
    } else {
      console.log("NewReagentFormContent - No file selected or files array empty");
      handleFileChange(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Name */}
      <FormField
        id="name"
        label="Reagent Name"
        value={formData.name}
        onChange={handleChange}
        placeholder="e.g., Sodium Chloride"
        required={true}
        error={errors.name}
      />
      
      {/* Catalog Number */}
      <FormField
        id="catalogNumber"
        label="Catalog Number"
        value={formData.catalogNumber}
        onChange={handleChange}
        placeholder="e.g., S7653"
        required={true}
        error={errors.catalogNumber}
      />
      
      {/* Category */}
      <CategorySelect
        value={formData.category}
        onChange={(value) => handleSelectChange("category", value)}
        categories={categories}
        error={errors.category}
      />
      
      {/* Amount and Unit */}
      <AmountInput
        amount={formData.amount || ""}
        unit={formData.unit || ""}
        onAmountChange={(value) => handleSelectChange("amount", value)}
        onUnitChange={(value) => handleSelectChange("unit", value)}
      />
      
      {/* Number of Packages */}
      <FormField
        id="numberOfPackages"
        label="Nr of packages/containers (optional)"
        value={formData.numberOfPackages || ""}
        onChange={handleChange}
        placeholder="e.g., 3"
        required={false}
        type="number"
      />
      
      {/* Expiry Date */}
      <DatePickerField
        id="expiryDate"
        label="Expiry Date"
        value={formData.expiryDate || null}
        onChange={(date) => handleDateChange("expiryDate", date)}
        error={errors.expiryDate}
      />
      
      {/* Description */}
      <DescriptionField
        value={formData.description || ""}
        onChange={handleChange}
      />
      
      {/* Storage Conditions */}
      <StorageConditionsField
        value={formData.storageConditions || ""}
        onChange={handleChange}
      />
      
      {/* Image Upload */}
      <ImageUploadField
        onChange={handleImageUploadChange}
      />
    </div>
  );
};

export default NewReagentFormContent;
